import { ref, computed, onMounted } from 'vue';
import Sidebar from '@/components/Sidebar.vue';
import Header from '@/components/HeaderDashboard.vue';
import Swal from 'sweetalert2';
import { hasPermission } from '@/store/userStore';
import { DepartamentoService } from '@/services/DepartamentoService';
import { PlusIcon, EditIcon, TrashIcon, SearchIcon, ChevronLeftIcon, ChevronRightIcon, XIcon, DownloadIcon, FileTextIcon, GripVerticalIcon, InfoIcon, PowerIcon } from 'lucide-vue-next';
import draggable from 'vuedraggable';
export default {
    name: 'Departamentos',
    components: { Sidebar, Header, PlusIcon, EditIcon, TrashIcon, SearchIcon, ChevronLeftIcon, ChevronRightIcon, XIcon, DownloadIcon, FileTextIcon, GripVerticalIcon, InfoIcon, PowerIcon, draggable },
    setup() {
        const departamentos = ref([]);
        const isLoading = ref(false);
        const isSubmitting = ref(false);
        const isModalOpen = ref(false);
        const isEditing = ref(false);
        const currentId = ref(null);
        const searchQuery = ref('');
        const currentPage = ref(1);
        const itemsPerPage = 10;
        const currentForm = ref({ nombre: '' });
        // Export Excel State
        const isExportModalOpen = ref(false);
        const isExporting = ref(false);
        const availableColumns = ref([
            { id: 1, name: "Nombre" }
        ]);
        const exportConfig = ref({
            format: 'excel',
            selectedColumns: ["Nombre"],
            sortColumn: "Nombre",
            sortDirection: "asc"
        });
        const filteredDepartamentos = computed(() => {
            const q = searchQuery.value.toLowerCase();
            return departamentos.value.filter(d => d.nombre.toLowerCase().includes(q));
        });
        const totalPages = computed(() => Math.max(1, Math.ceil(filteredDepartamentos.value.length / itemsPerPage)));
        const paginatedItems = computed(() => {
            const start = (currentPage.value - 1) * itemsPerPage;
            return filteredDepartamentos.value.slice(start, start + itemsPerPage);
        });
        function formatDate(dateStr) {
            if (!dateStr)
                return '—';
            return new Date(dateStr).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
        }
        async function loadItems() {
            isLoading.value = true;
            try {
                departamentos.value = await DepartamentoService.getAll();
            }
            catch {
                Swal.fire({ title: 'Error', text: 'No se pudieron cargar los departamentos.', icon: 'error' });
            }
            finally {
                isLoading.value = false;
            }
        }
        function openModal(dep) {
            if (dep) {
                currentForm.value = { nombre: dep.nombre };
                currentId.value = dep.departamentoID;
                isEditing.value = true;
            }
            else {
                currentForm.value = { nombre: '' };
                currentId.value = null;
                isEditing.value = false;
            }
            isModalOpen.value = true;
        }
        function closeModal() { isModalOpen.value = false; }
        // Export functions
        function openExportModal() {
            isExportModalOpen.value = true;
        }
        function closeExportModal() {
            isExportModalOpen.value = false;
        }
        async function handleExport() {
            if (exportConfig.value.selectedColumns.length === 0) {
                Swal.fire({ title: 'Atención', text: 'Debe seleccionar al menos una columna.', icon: 'warning' });
                return;
            }
            isExporting.value = true;
            try {
                await DepartamentoService.exportReport({
                    searchTerm: searchQuery.value,
                    selectedColumns: availableColumns.value
                        .map(c => c.name)
                        .filter(name => exportConfig.value.selectedColumns.includes(name)),
                    format: exportConfig.value.format,
                    sortColumn: exportConfig.value.sortColumn,
                    sortDirection: exportConfig.value.sortDirection
                });
                closeExportModal();
                Swal.fire({ title: '¡Éxito!', text: 'Reporte generado correctamente.', icon: 'success', timer: 2000, showConfirmButton: false });
            }
            catch (error) {
                Swal.fire({ title: 'Error', text: 'No se pudo generar el reporte.', icon: 'error' });
            }
            finally {
                isExporting.value = false;
            }
        }
        async function saveItem() {
            isSubmitting.value = true;
            try {
                const payload = {
                    departamentoID: currentId.value ?? 0,
                    nombre: currentForm.value.nombre,
                    fechaCreacion: new Date().toISOString(),
                };
                if (isEditing.value && currentId.value !== null) {
                    await DepartamentoService.update(currentId.value, payload);
                    Swal.fire({ title: '¡Actualizado!', text: 'Departamento actualizado correctamente.', icon: 'success', timer: 2000, showConfirmButton: false });
                }
                else {
                    await DepartamentoService.add(payload);
                    Swal.fire({ title: '¡Registrado!', text: 'Departamento agregado correctamente.', icon: 'success', timer: 2000, showConfirmButton: false });
                }
                closeModal();
                await loadItems();
            }
            catch {
                Swal.fire({ title: 'Error', text: 'Hubo un problema al guardar el departamento.', icon: 'error' });
            }
            finally {
                isSubmitting.value = false;
            }
        }
        async function confirmDelete(id) {
            const res = await Swal.fire({
                title: '¿Desea eliminar este departamento?',
                text: 'Esta acción no se puede deshacer.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3b82f6',
                cancelButtonColor: '#94a3b8',
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar',
            });
            if (res.isConfirmed) {
                try {
                    await DepartamentoService.delete(id);
                    Swal.fire({ title: '¡Eliminado!', text: 'El departamento ha sido eliminado.', icon: 'success', timer: 2000, showConfirmButton: false });
                    await loadItems();
                }
                catch (e) {
                    Swal.fire({
                        title: 'No se puede eliminar',
                        text: e?.message || 'No se pudo eliminar el departamento.',
                        icon: 'error',
                        confirmButtonText: 'Entendido'
                    });
                }
            }
        }
        function prevPage() { if (currentPage.value > 1)
            currentPage.value--; }
        function nextPage() { if (currentPage.value < totalPages.value)
            currentPage.value++; }
        async function toggleActive(dep) {
            const result = await Swal.fire({
                title: '¿Cambiar estado?',
                text: `¿Estás seguro de que deseas ${dep.active ? 'desactivar' : 'activar'} el departamento "${dep.nombre}"?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sí, cambiar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#3b82f6',
                cancelButtonColor: '#94a3b8',
            });
            if (!result.isConfirmed)
                return;
            try {
                await DepartamentoService.toggleActivo(dep.departamentoID);
                dep.active = !dep.active;
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: `Departamento ${dep.active ? 'activado' : 'desactivado'}`,
                    showConfirmButton: false,
                    timer: 2000
                });
            }
            catch {
                Swal.fire({ title: 'Error', text: 'No se pudo cambiar el estado.', icon: 'error' });
            }
        }
        onMounted(loadItems);
        return {
            hasPermission,
            departamentos, isLoading, isSubmitting, isModalOpen, isEditing, currentForm, searchQuery,
            filteredDepartamentos, paginatedItems, currentPage, totalPages, itemsPerPage,
            openModal, closeModal, saveItem, confirmDelete, prevPage, nextPage, formatDate,
            isExportModalOpen, isExporting, availableColumns, exportConfig, openExportModal, closeExportModal, handleExport,
            toggleActive
        };
    },
};
